import { useState } from "react";
import { Button, Eyebrow } from "@/components/ui";

interface Chapter {
  order: number;
  title: string;
  content: string;
  word_count: number;
}

interface ChapterReaderProps {
  chapters: readonly Chapter[];
  bookTitle: string;
  authorName: string;
}

/**
 * ChapterReader — Archival reader surface.
 *
 * Sidebar of chapters at left (paper-2 sunken tone); manuscript
 * page on paper-raised at center, set in Source Serif 4 at editorial
 * size with generous leading. Verdigris spine on the active chapter row.
 */
export function ChapterReader({ chapters, bookTitle, authorName }: ChapterReaderProps) {
  const [activeChapter, setActiveChapter] = useState(0);
  const totalWords = chapters.reduce((sum, ch) => sum + ch.word_count, 0);
  const ch = chapters[activeChapter];

  return (
    <div className="flex min-h-[calc(100dvh-150px)] flex-col bg-paper lg:h-[calc(100dvh-150px)] lg:flex-row">
      <aside className="max-h-72 w-full flex-shrink-0 overflow-y-auto border-b border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised/80 backdrop-blur-2xl lg:max-h-none lg:w-64 lg:border-b-0 lg:border-r">
        <div className="border-b border-rule p-4">
          <Eyebrow>Manuscript</Eyebrow>
          <h2 className="font-display font-semibold text-t-18 leading-as-snug text-ink-1 mt-[6px] truncate">
            {bookTitle}
          </h2>
          <p className="font-mono text-[11px] tracking-as-eyebrow uppercase text-ink-3 mt-[4px]">
            {authorName} · {totalWords.toLocaleString()} words
          </p>
        </div>
        <nav className="grid gap-1 p-2 sm:grid-cols-2 lg:block">
          {chapters.map((c, idx) => {
            const active = idx === activeChapter;
            return (
              <button
                key={c.order}
                onClick={() => setActiveChapter(idx)}
                className={
                  "block w-full text-left rounded-as-2 px-3 py-2 transition-colors duration-as-1 " +
                  (active
                    ? "bg-paper-raised border-l-[3px] border-walnut pl-[10px]"
                    : "hover:bg-paper-raised/60")
                }
              >
                <div className="font-display font-semibold text-t-16 leading-as-snug text-ink-1 truncate">
                  Ch. {c.order} · {c.title}
                </div>
                <div className="font-mono text-[10.5px] tracking-as-eyebrow uppercase text-ink-3 mt-[2px]">
                  {c.word_count.toLocaleString()} words
                </div>
              </button>
            );
          })}
        </nav>
      </aside>

      <main className="flex-1 overflow-y-auto bg-[linear-gradient(180deg,var(--as-paper),var(--as-paper-2))]">
        <div className="mx-auto my-4 max-w-[680px] rounded-as-3 border border-[color:color-mix(in_oklab,var(--as-paper-raised)_76%,transparent)] bg-paper-raised px-5 py-10 shadow-as-2 sm:my-6 sm:px-10 lg:my-8 lg:py-12">
          {ch && (
            <>
              <Eyebrow>
                Chapter {ch.order} · {ch.word_count.toLocaleString()} words
              </Eyebrow>
              <h1 className="font-display font-bold text-t-37 leading-as-tight tracking-as-tight text-ink-1 mt-[8px] mb-[24px]">
                {ch.title}
              </h1>
              <div className="font-text text-t-18 leading-as-read text-ink-2">
                {ch.content.split("\n\n").map((para, i) => (
                  <p key={i} className="mb-[18px]">{para}</p>
                ))}
              </div>
            </>
          )}
          <div className="mt-12 flex flex-col justify-between gap-3 border-t border-rule pt-6 sm:flex-row">
            <Button
              variant="secondary"
              onClick={() => setActiveChapter(Math.max(0, activeChapter - 1))}
              disabled={activeChapter === 0}
              className={activeChapter === 0 ? "invisible" : ""}
            >
              Previous chapter
            </Button>
            <Button
              variant="secondary"
              onClick={() => setActiveChapter(Math.min(chapters.length - 1, activeChapter + 1))}
              disabled={activeChapter === chapters.length - 1}
              className={activeChapter === chapters.length - 1 ? "invisible" : ""}
            >
              Next chapter
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
